import {
  fetchEnrichEntries,
  fetchReportImageUrls,
  saveEnrichThemes,
} from '../api/account-report';
import { openaiApi } from '../../../shared/api/openai.api';
import { mapWithConcurrency } from '../../../shared/lib/mapWithConcurrency';

import { captionTheme } from './caption-theme';
import { detectLanguage } from './detect-language';
import {
  isCacheableImageUrl,
  loadReportCache,
  planReportWork,
  saveReportCache,
} from './report-cache';

/**
 * Превью и тема публикации — общий шаг всех отчётов.
 *
 * Обе величины стоят денег и времени: превью перезаливается в Storage (ссылки
 * Instagram и TikTok протухают), тему распознаёт GPT по кадру. Обе неизменны
 * для публикации, поэтому считаются один раз и живут в кэше — локальном и
 * общем командном.
 *
 * Вынесено из AccountsStatsPage, когда те же шаги понадобились отчёту «Топ
 * видео». Держать две реализации распознавания нельзя: они разошлись бы в
 * фолбэках и в том, что кладут в общий кэш, а платим мы за каждый вызов.
 */

/**
 * akorda.news пишет текст в подписи, а не накладывает на кадр: для него тема —
 * заголовок, который GPT делает из описания (OCR кадра бесполезен).
 */
const CAPTION_THEME_ACCOUNTS = new Set(['akorda.news']);

export function usesCaptionHeadline(username: string | undefined): boolean {
  return CAPTION_THEME_ACCOUNTS.has((username ?? '').replace(/^@/, '').toLowerCase());
}

export type EnrichInput = {
  mediaId: string;
  thumbnailUrl?: string;
  /** Нужна только там, где тема берётся из подписи, а не с кадра. */
  caption?: string;
};

export type EnrichEntry = { theme: string; language: string; imageUrl: string };

export type EnrichOptions = {
  /**
   * Тема из подписи вместо OCR — для аккаунтов, которые пишут текст в подписи,
   * а не накладывают на кадр (распознавать там нечего).
   */
  useCaptionHeadline?: boolean;
  /** Счётчик считает только реальную работу: то, что нашлось в кэше, не в счёт. */
  onProgress?: (done: number, total: number) => void;
};

// Шаг упирается в чужой сервис, а не в наш процессор, поэтому окно широкое.
// Выше не поднимаю: у OpenAI свои лимиты на частоту.
const THEME_CONCURRENCY = 12;

export async function enrichReportRows(
  rows: EnrichInput[],
  options: EnrichOptions = {},
): Promise<Map<string, EnrichEntry>> {
  const cache = loadReportCache();

  // Общий кэш команды: подмешиваем посчитанное другими сотрудниками до
  // планирования работы — их темы и перезалитые превью не считаются заново.
  const serverEntries = await fetchEnrichEntries(rows.map((r) => r.mediaId));
  for (const [id, entry] of Object.entries(serverEntries)) {
    const local = { ...cache.get(id) };
    if (!local.imageUrl && entry.imageUrl) local.imageUrl = entry.imageUrl;
    if (local.theme === undefined && entry.theme !== undefined) {
      local.theme = entry.theme;
      local.language = entry.theme ? detectLanguage(entry.theme) : '';
    }
    cache.set(id, local);
  }

  const captionById = new Map<string, string>();
  if (options.useCaptionHeadline) {
    for (const r of rows) {
      const caption = String(r.caption ?? '').trim();
      if (caption) captionById.set(r.mediaId, caption);
    }
  }

  // Фаза 1 — перезалить превью в Storage, фаза 2 — распознать тему.
  const plan = planReportWork(
    rows.map((r) => ({ mediaId: r.mediaId, thumbnailUrl: r.thumbnailUrl || undefined })),
    cache,
  );
  const imageById = new Map(plan.imagesFromCache);
  const themeById = new Map(plan.themesFromCache);

  let workDone = 0;
  const bumpProgress = () => options.onProgress?.(++workDone, plan.totalWork);
  options.onProgress?.(0, plan.totalWork);

  if (plan.imagesToFetch.length > 0) {
    const sourceById = new Map(plan.imagesToFetch.map((i) => [i.id, i.thumbnailUrl]));
    await fetchReportImageUrls(plan.imagesToFetch, {
      onResult: (r) => {
        imageById.set(r.id, r.imageUrl);
        // В кэш — только перезалитое: подробности в isCacheableImageUrl.
        if (isCacheableImageUrl(r.imageUrl, sourceById.get(r.id))) {
          cache.set(r.id, { ...cache.get(r.id), imageUrl: r.imageUrl });
        }
      },
      onProgress: () => bumpProgress(),
    });
  }

  const themeTasks = rows
    .filter((r) => !themeById.has(r.mediaId))
    .map((r) => ({
      id: r.mediaId,
      caption: captionById.get(r.mediaId) ?? '',
      imageUrl: imageById.get(r.mediaId) || r.thumbnailUrl || '',
    }))
    .filter((t) => t.caption || t.imageUrl);

  // Свежераспознанное уходит и в общий кэш — фолбэки туда не пишем,
  // иначе заглушка застрянет у всей команды навсегда.
  const newThemes: { id: string; theme: string }[] = [];
  await mapWithConcurrency(
    themeTasks,
    THEME_CONCURRENCY,
    async ({ id, caption, imageUrl }) => {
      let theme: string;
      try {
        const raw = caption
          ? (await openaiApi.captionHeadline(caption)).headline
          : (await openaiApi.extractOverlayText(imageUrl)).text;
        theme = raw.replace(/\s+/g, ' ').trim();
      } catch {
        // GPT недоступен — не терять строку: первый абзац описания как есть.
        const fallback = captionTheme(caption);
        themeById.set(id, { theme: fallback, language: fallback ? detectLanguage(fallback) : '' });
        return;
      }
      const entry = { theme, language: detectLanguage(theme) };
      themeById.set(id, entry);
      cache.set(id, { ...cache.get(id), theme: entry.theme, language: entry.language });
      newThemes.push({ id, theme: entry.theme });
    },
    () => bumpProgress(),
  );

  saveReportCache(cache);
  void saveEnrichThemes(newThemes);

  const result = new Map<string, EnrichEntry>();
  for (const r of rows) {
    const theme = themeById.get(r.mediaId)?.theme ?? '';
    result.set(r.mediaId, {
      theme,
      language: themeById.get(r.mediaId)?.language ?? (theme ? detectLanguage(theme) : ''),
      imageUrl: imageById.get(r.mediaId) || r.thumbnailUrl || '',
    });
  }
  return result;
}
